import financialApi from 'api/requests/financial';
import CurrencyInputField from 'components/_UI/CurrencyInputField';
import Loader from 'components/_UI/Loader';
import Modal from 'components/_UI/Modal';
import { useWithdraw } from 'context/financial/Withdraw';
import currency from 'helpers/currency';
import React, { useState } from 'react'
import { AlertCircle, ArrowDownCircle, DollarSign, Info } from 'react-feather';
import styled from 'styled-components';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';

let convertWithValue = null

export default function WithdrawModal({ visible, onClose, onSucess }) {
    const { withdraw, setWithdraw } = useWithdraw();

    const [withdrawValue, setWithdrawValue] = useState();
    const [invalidField, setInvalidField] = useState(false);
    const [loading, setLoading] = useState(false);

    function parseValue(raw) {
        if (!raw) return 0;
        return Number(raw.replace(',', '.'));
    }

    function validateOnChange(raw) {
        setInvalidField(false);

        if (!raw) return;

        const num = parseValue(raw);
        if (isNaN(num) || num <= 0) return;

        if (num > withdraw?.availableValue) {
            setInvalidField(`O valor disponível para saque é ${currency.format(withdraw.availableValue)}`);
        } else if (num > 0 && num < 5) {
            setInvalidField('O valor mínimo de saque é R$ 5,00');
        }
    }

    function isValidValue() {
        setInvalidField(false);

        if (!withdrawValue) {
            setInvalidField('Preencha o campo para efetuar o saque!')
            return;
        }

        convertWithValue = parseValue(withdrawValue);
        if (convertWithValue < 0 || isNaN(convertWithValue)) {
            setInvalidField('Insira um valor valido')
            return false;
        }

        if (convertWithValue > withdraw.availableValue) {
            setInvalidField(`O valor disponível para saque é ${currency.format(withdraw.availableValue)}`)
            return false;
        }

        if (convertWithValue < 5) {
            setInvalidField('O valor mínimo de saque é R$ 5,00')
            return false
        }

        return true;
    }

    async function onWithdraw() {
        if (!isValidValue()) {
            return
        }

        setLoading(true);

        try {
            await financialApi.withdraw({ value: convertWithValue });
            setWithdraw({ ...withdraw, success: Math.random(Infinity) });
            onSucess()
            onClose();
        } catch (error) {
            setInvalidField('Ocorreu um erro ao fazer o saque, tente novamente.')
        } finally {
            setLoading(false);
        }
    }

    const canWithdraw = withdraw?.availableValue > 0 && withdrawValue;

    return (
        <Modal isOpen={visible} onClose={() => onClose(false)}>
            <Wrapper>
                <Header>
                    <IconCircle>
                        <ArrowDownCircle size={22} color={colors.occasionalPurple} />
                    </IconCircle>
                    <Title>Efetuar saque</Title>
                </Header>

                <BalanceCard>
                    <BalanceLabel>
                        <DollarSign size={14} />
                        Saldo disponível
                    </BalanceLabel>
                    <BalanceValue>
                        {currency.format(withdraw?.availableValue)}
                    </BalanceValue>
                </BalanceCard>

                <InputSection>
                    <InputLabel>Valor do saque</InputLabel>
                    <InputWrapper hasError={!!invalidField}>
                        <CurrencyInputField
                            placeholder='R$ 0,00'
                            layout='straight'
                            onChange={e => {
                                setWithdrawValue(e);
                                validateOnChange(e);
                            }}
                        />
                    </InputWrapper>

                    {invalidField && !loading && (
                        <ErrorMessage>
                            <AlertCircle size={14} />
                            {invalidField}
                        </ErrorMessage>
                    )}

                    <HintRow>
                        <Info size={13} color={colors.night + '88'} />
                        Valor mínimo de saque: R$ 5,00
                    </HintRow>
                </InputSection>

                {loading ? (
                    <LoaderWrapper>
                        <Loader />
                    </LoaderWrapper>
                ) : (
                    <Footer>
                        <CancelButton onClick={() => onClose(false)}>
                            Cancelar
                        </CancelButton>
                        <ConfirmButton
                            disabled={!canWithdraw}
                            onClick={onWithdraw}
                        >
                            <ArrowDownCircle size={18} />
                            Efetuar saque
                        </ConfirmButton>
                    </Footer>
                )}
            </Wrapper>
        </Modal>
    )
}

const Wrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
`;

const IconCircle = styled.div`
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: ${colors.occasionalPurple}11;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const Title = styled.h2`
    font-size: 20px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0;
`;

const BalanceCard = styled.div`
    background: linear-gradient(135deg, ${colors.occasionalPurple}0D, ${colors.occasionalPurple}05);
    border: 1px solid ${colors.occasionalPurple}22;
    border-radius: 12px;
    padding: 16px 20px;
    margin-bottom: 24px;
`;

const BalanceLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
    margin-bottom: 6px;
`;

const BalanceValue = styled.div`
    font-size: 28px;
    font-weight: 700;
    color: ${colors.occasionalPurple};
    letter-spacing: -0.5px;
`;

const InputSection = styled.div`
    margin-bottom: 24px;
`;

const InputLabel = styled.label`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 6px;
    display: block;
`;

const InputWrapper = styled.div`
    /* Override the CurrencyInputField straight style */
    input {
        width: 100%;
        background: ${colors.night}08 !important;
        border: 1.5px solid ${({ hasError }) => hasError ? colors.danger : colors.night + '22'} !important;
        border-bottom: 1.5px solid ${({ hasError }) => hasError ? colors.danger : colors.night + '22'} !important;
        border-radius: 10px !important;
        padding: 14px 16px !important;
        font-size: 18px !important;
        font-weight: 600 !important;
        color: ${colors.nightDark} !important;
        transition: all 0.15s ease !important;

        &::placeholder {
            color: ${colors.night}55 !important;
            font-weight: 400 !important;
        }

        &:focus {
            border-color: ${({ hasError }) => hasError ? colors.danger : colors.occasionalPurple} !important;
            box-shadow: 0 0 0 3px ${({ hasError }) => hasError ? colors.danger + '18' : colors.occasionalPurple + '18'} !important;
            background: #fff !important;
        }
    }

    /* Hide the default label inside CurrencyInputField */
    label {
        display: none;
    }
`;

const ErrorMessage = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    color: ${colors.danger};
    margin-top: 8px;
    padding: 6px 10px;
    background: ${colors.danger}0A;
    border-radius: 8px;
`;

const HintRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: ${colors.night}88;
    margin-top: 10px;
`;

const LoaderWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 20px 0;
`;

const Footer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 18px;
    border-top: 1px solid ${colors.night}11;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        justify-content: flex-end;
    }
`;

const CancelButton = styled.button`
    padding: 10px 20px;
    border: 1px solid ${colors.night}22;
    border-radius: 10px;
    background: transparent;
    color: ${colors.night};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    order: 2;

    @media screen and (min-width: ${breakpoints.md}) {
        order: 1;
    }

    &:hover {
        background: ${colors.night}08;
        border-color: ${colors.night}44;
    }
`;

const ConfirmButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 24px;
    border: none;
    border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.night + '18' : colors.occasionalPurple};
    color: ${({ disabled }) => disabled ? colors.night + '66' : '#fff'};
    font-size: 14px;
    font-weight: 700;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.15s ease;
    order: 1;

    @media screen and (min-width: ${breakpoints.md}) {
        order: 2;
    }

    &:hover:not(:disabled) {
        background: ${colors.secondaryPurple};
    }
`;
